/**
 * Invoicing configuration strings.
 *
 * These are config options (not schema fields). The help text (h_) was migrated
 * from the inline renderHelpLabel JSX in
 * src/Configuration/Invoicing/InvoiceConfig.tsx — verify against the implementation.
 *
 * The value labels in roundingMethod / interestMethod / interestRate mirror the enum
 * display strings in src/Configuration/GeneralOptions/OptionConstants.ts
 * (RoundingMethods, InterestMethods) — keep them in sync.
 */
export default {
    d_defaultSummary: 'Default Summary',
    h_defaultSummary: "A default entry for every invoice's Summary field. This is the note that is displayed to clients on the invoice. This is used as a default, and can be modified when editing the invoice.",

    d_narrativeHeader: 'Narrative Header',
    h_narrativeHeader: 'There are two types of invoices: detailed and narrative. The ABA recommends narrative-based invoices (see <a href="https://www.americanbar.org/products/inv/book/306735234/">here</a>). The Narrative Header refers to the title just before the narrative, e.g. Work We Did for You.',

    d_letterheadWidth: 'Letterhead Width',
    h_letterheadWidth: 'How wide the letterhead image prints on invoices, as a percentage of the page width inside the margins. At 100%, the image fills the 7.5 inches between the margins of a letter-size page. The image keeps its proportions. Until you move the slider, the image prints at its own size. Finalized invoices keep the width they had when they were finalized.',

    d_billingIncrement: 'Billing Increment in Minutes',
    h_billingIncrement: '<div>For example, entering 6 here means that we bill in 6 minute increments (.1 hours)</div><div>Default is to bill per 1 minute</div>',

    d_roundingMethod: 'Rounding Method',
    h_roundingMethod: '<div>Rounding method to apply on invoices. Examples assume a 6-minute (.1 hour) increment.</div><ul><li>Always Round Up: always rounds up to the next whole billing increment<br/><span class="example">Example: 9 minutes rounds up to 12 minutes (.2 hours)</span></li><li>Always Round Down: always rounds down to the last whole billing increment<br/><span class="example">Example: 9 minutes rounds down to 6 minutes (.1 hours)</span></li><li>Round Up Past Halfway Point: round up when passing the halfway between billing increments<br/><span class="example">Example: 9 minutes rounds down to 6 minutes, but 9 minutes and 1 second rounds up 12 minutes</span></li></ul>',

    d_taxRate: 'Tax Rate',
    h_taxRate: 'Enter a sales tax rate if your state requires it (e.g. NM, SD, HI). Set to 0 to disable.',

    d_taxLabel: 'Tax Label',
    h_taxLabel: 'Label to display on invoices next to the tax amount, e.g. "Sales Tax" or "GRT".',

    // Label and help flip with the selected Interest Method (flat monthly fee vs. a rate).
    d_interestRate: (params: {flatMonthly?: boolean}) =>
        params?.flatMonthly ? 'Monthly Fee ($)' : 'Interest Rate',
    h_interestRate: (params: {flatMonthly?: boolean}) =>
        params?.flatMonthly
            ? 'Flat dollar amount charged per month on overdue balances.'
            : 'Monthly interest rate (%) applied to overdue balances. Set to 0 to disable.',

    d_interestMethod: 'Interest Method',
    h_interestMethod: '<ul><li>Simple Daily: accrues daily based on a monthly rate</li><li>Simple Monthly: applies rate per whole month overdue</li><li>Flat Monthly Fee: charges a fixed dollar amount per month</li></ul>',

    d_leadPeriod: 'Lead Invoice Period',
    h_leadPeriod: '<div>How a consultation (lead) invoice shows its period.</div><ul><li>Up to Consult Date: shows "Up to" and the lead\'s consult date</li><li>Full Range: shows the invoice\'s start and end dates, when it has both</li><li>Hide It: shows no period</li></ul><div>Finalized invoices keep the period they had when they were finalized.</div>',

    d_gracePeriod: 'Grace Period (Days)',
    h_gracePeriod: 'Number of days after the due date before interest begins accruing. Set to 0 for no grace period.',
};
